<template>
  <footer id="discussionFooter" class="article__footer">
    <button
      class="discussion-btn thumbs-up"
      :disabled="makingApiCall ? true : undefined"
      @click.prevent="toggleLike"
    >
      <span aria-hidden="true" :class="likeClass" />
      {{ likeCount }} Like
    </button>

    <div v-if="showMoreOptions" :class="['more-options', { 'multiple-options': isOwner }]">
      <button v-if="isOwner" class="btn -link" @click="redirectEdit">Edit Discussion</button>
      <button v-if="isOwner" class="btn -link" @click="openConfirmModal">Remove Discussion</button>
      <button v-if="!isOwner" class="btn -link" @click="openFlagModal">Report Spam or Abuse</button>
    </div>

    <button
      v-if="user.isLoggedIn"
      class="btn-more"
      @click="() => (showMoreOptions = !showMoreOptions)"
    >
      <i class="f" />
      <i class="m" />
      <i class="l" />
    </button>
  </footer>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useEntityContextStore } from '@/stores/entity-context'
import { fbApi } from '@/utils/http-api'

export default defineComponent({
  components: {},
  props: {
    authorId: {
      type: Number,
      require: true,
    },
    // used for delete
    initLikeId: {
      type: Number,
      default: 0,
    },
    initLikeCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showMoreOptions: false,
      makingApiCall: false,

      // initialize from props
      likeId: this.initLikeId,
      likeCount: this.initLikeCount,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['user']),
    ...mapState(useEntityContextStore, ['entity']),

    isOwner() {
      return this.user.id === this.authorId
    },
    likeClass() {
      // loading states
      if (this.makingApiCall && !this.likeId) {
        return 'iconfont-like-select'
      }

      if (this.makingApiCall && this.likeId) {
        return 'iconfont-like'
      }

      // non-loading states
      return `iconfont${this.likeId ? '-like-select' : '-like'}`
    },

    flagModalId() {
      return `flag-discussion-${this.entity.id}-modal`
    },
  },
  methods: {
    ...mapActions(useModalsStore, ['showLoginModal', 'openSimpleModal']),

    redirectEdit() {
      window.location = `/community/edit/${this.entity.id}`
    },

    // similar function in like-react-form.vue
    async toggleLike() {
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      this.makingApiCall = true

      if (!this.likeId) {
        // post
        try {
          // instantly increment count
          this.likeCount += 1

          const res = await fbApi.post('/like', {
            json: {
              likeable_type: this.entity.type,
              likeable_id: this.entity.id,
              //TODO: CI - Comment Improvement Feature - Since likes now support a react type
              // passing in 1 for discussion likes as the default 👍.
              react_type_id: 1,
            },
          })

          if (!res || !res.body || !res.body.data) {
            new Error(`Unable to update like, please try again`)
          }

          this.likeId = res.body.data.id
        } catch (err) {
          // reset like count
          this.likeCount -= 1
        }
      } else {
        // delete
        try {
          // decrement count
          this.likeCount = this.likeCount > 0 ? this.likeCount - 1 : 0

          const res = await fbApi.delete(`/like/${this.likeId}`, {
            json: {
              likeable_type: this.entity.type,
              likeable_id: this.entity.id,
              //TODO: CI - Comment Improvement Feature - Since likes now support a react type
              // passing in 1 for discussion likes as the default 👍.
              react_type_id: 1,
            },
          })

          if (!res || !res.body || !res.body.data) {
            new Error(`Unable to update like, please try again`)
          }

          // clear out
          this.likeId = 0
        } catch (err) {
          // set like count
          this.likeCount += 1
        }
      }

      this.makingApiCall = false
    },

    openFlagModal() {
      this.showMoreOptions = false

      this.openSimpleModal({
        type: 'FLAG',
        id: this.flagModalId,
        props: {
          flaggableId: this.entity.id,
          flaggableType: 'discussion',
        },
      })
    },

    openConfirmModal() {
      this.showMoreOptions = false

      this.openSimpleModal({
        type: 'CONFIRM',
        props: {
          heading: 'Remove Discussion',
          subheading:
            'Are you sure you want to remove this discussion?  All comments on it will be removed as well.',
          buttonText: 'Remove',
          url: `/community/remove/${this.entity.id}`,
        },
      })
    },
  },
})
</script>
